/* Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved. */

// ── Right-panel router ─────────────────────────────────────────────────────────
//
// The single, statically-typed source of truth for WHICH surface (if any)
// occupies the docked right rail (WIDE) / fullscreen overlay (NARROW). The panel
// used to be hard-wired to chat; now its content is chosen by this route. Adding
// a surface is a small, type-checked change: extend `RightPanelSurface` and mount
// a panel that shows on its route.
//
// The panel is an OVERLAY, not a URL route: WIDE docks it as a right rail beside
// `#main-content` (which reserves its width while any surface is open); NARROW
// fills the page over what you were on. Either way, closing reveals the page
// underneath (no navigation). It is closed on a fresh load — opening is always an
// explicit user action — so nothing is persisted.

import {
    combineData,
    data,
    type MaybeObservableValue,
    type ObservableValue,
} from '@amazon/vinyl-observable'
import { createDisposer, type Unsubscribe } from '@amazon/vinyl-util'
import { installRightPanelResizeHandle } from '@/rightPanel/RightPanelResizeHandle'
import { mediaQuery$ } from '@/util/mediaQuery'
import { registerFullScreenOverlay } from '@/util/fullScreenOverlayRegistry'
import { globalData } from '@amzn/bf-playback'

/**
 * The surfaces that can occupy the right panel. Extend this union (and mount a
 * panel that shows on the new route) to add a surface — every setter and consumer
 * below stays type-checked against it.
 */
export type RightPanelSurface = 'chat' | 'queue' | 'podcastAbout'

const route$ = globalData(() => data<RightPanelSurface | null>(null))

/** Which surface occupies the right panel, or `null` when it's closed. */
export const rightPanelRoute$: ObservableValue<RightPanelSurface | null> = route$

/** Show `surface` in the right panel, replacing whatever was there. */
export function openRightPanel(surface: RightPanelSurface): void {
    route$.value = surface
}

/** Close the right panel, revealing the page underneath (no route change). */
export function closeRightPanel(): void {
    route$.value = null
}

/** Toggle `surface`: show it if hidden, close the panel if it's already shown. */
export function toggleRightPanel(surface: RightPanelSurface): void {
    route$.value = route$.value === surface ? null : surface
}

/** Reactive `true` while `surface` is the one occupying the right panel. */
export function rightPanelShowing$(surface: RightPanelSurface): ObservableValue<boolean> {
    return route$.map((current) => current === surface)
}

// Fallback flip width (px) used only when the `--chat-pane-wide-min-width` :root
// token can't be read (SSR / test host with no layout). The live value comes from
// that token, which styles.scss derives from the single Sass source
// `mixins.$chat-pane-wide` — so the JS "is fullscreen?" check and the CSS layout
// flip stay on one breakpoint.
const RIGHT_PANEL_WIDE_MIN_WIDTH_FALLBACK = 650

function rightPanelWideQuery(): string {
    const raw =
        typeof getComputedStyle === 'function'
            ? getComputedStyle(document.documentElement).getPropertyValue(
                  '--chat-pane-wide-min-width'
              )
            : ''
    return `(min-width: ${parseFloat(raw) || RIGHT_PANEL_WIDE_MIN_WIDTH_FALLBACK}px)`
}

/**
 * Reactive `true` while the WIDE docked-rail layout applies, off the same
 * breakpoint {@link isRightPanelFullscreen} reads — so a surface that only makes
 * sense as a rail (and never as a fullscreen takeover) can gate its affordance
 * on the same seam the CSS flips at.
 */
export function rightPanelWide$(): ObservableValue<boolean> {
    return mediaQuery$(rightPanelWideQuery())
}

/**
 * `true` when the panel, if open, is covering the whole page (NARROW layout) — as
 * opposed to the WIDE docked right rail. Read from the same breakpoint the panel's
 * fullscreen CSS uses, so close-on-navigate only fires for the fullscreen overlay
 * case and never collapses the persistent wide rail.
 */
export function isRightPanelFullscreen(): boolean {
    if (typeof matchMedia !== 'function') return false
    return !matchMedia(rightPanelWideQuery()).matches
}

/**
 * Wire the shared docked-rail / fullscreen-overlay chrome onto a right-panel
 * surface element: mount the shared left-edge resize handle (so EVERY surface is
 * resizable, not just chat), reflect its open state (`data-open`, `inert`), close
 * on Escape when focus is inside it, and collapse on navigation while it's the
 * NARROW fullscreen overlay. `resizeLabel` is the surface's own localized a11y
 * label for the handle. `onHidden` fires each time the surface goes closed (e.g.
 * to reset sub-views). Returns a disposer for `onConnect`.
 */
export function installRightPanelShell(
    el: HTMLElement,
    opts: {
        readonly surface: RightPanelSurface
        readonly resizeLabel: MaybeObservableValue<string>
        /** Also inert while open whenever this is true (e.g. hidden for inactivity). */
        readonly inertWhile$?: ObservableValue<boolean>
        readonly onHidden?: () => void
    }
): Unsubscribe {
    const { add, dispose } = createDisposer()
    const showing$ = rightPanelShowing$(opts.surface)

    // Shared docked-rail resize handle — mounted here so resizing applies to
    // every surface regardless of which one is active.
    add(installRightPanelResizeHandle(el, { label: opts.resizeLabel }))

    // Observable `data-*` bindings don't reflect, so drive the
    // CSS-visible open state imperatively. `inert` alone pulls a hidden surface
    // out of the a11y tree and tab order — it also blurs a focused descendant as
    // it's applied, so it can't trap focus. A bare `aria-hidden` can't: it hides
    // from AT without touching focus, so closing while a chrome button inside
    // still has focus trips the browser's "aria-hidden on a focused descendant"
    // warning. Don't set both — that races `aria-hidden` onto the element before
    // `inert` blurs it.
    // `onData` replays the current value on subscribe, so the initial state is
    // applied synchronously here — no separate seeding call.
    add(
        showing$.onData((open) => {
            el.setAttribute('data-open', open ? 'true' : 'false')
            if (!open) opts.onHidden?.()
        })
    )
    add(
        combineData({ open: showing$, forced: opts.inertWhile$ ?? data(false) }).onData(
            ({ open, forced }) => {
                if (open && !forced) el.removeAttribute('inert')
                else el.setAttribute('inert', '')
            }
        )
    )

    // Escape closes the panel when focus is inside it. Element-scoped (not a
    // document listener) so it can't swallow Escape in a modal or other overlay
    // whose subtree isn't a descendant of the surface.
    const onKeyDown = (event: KeyboardEvent) => {
        if (event.key !== 'Escape' || !showing$.value) return
        event.preventDefault()
        closeRightPanel()
    }
    el.addEventListener('keydown', onKeyDown)
    add(() => el.removeEventListener('keydown', onKeyDown))

    // Close on navigation only while this surface is the NARROW fullscreen
    // overlay; the WIDE docked rail persists across routes. Shared with the
    // FullPlayer via one registry so full-screen overlays collapse through one
    // pattern.
    add(
        registerFullScreenOverlay({
            isFullScreenNow: () => showing$.value && isRightPanelFullscreen(),
            close: closeRightPanel,
        })
    )
    return dispose
}

/**
 * Own the `rightPanelOpen`/`rightPanelChatOpen` body classes off the route (the
 * former set while ANY surface is open, the latter only for `chat`). It drives
 * the page's right-rail inset reservation and the bottom-chrome shift, so it
 * must live in the always-mounted app root — not the amp-gated chat surface —
 * for the queue rail to reserve space with the agent surface gated off. The
 * chat-specific class lets the input bar tell "become the pane's own composer"
 * (chat only) apart from "center within the narrowed main-content column"
 * (any other surface, e.g. queue).
 */
export function bindRightPanelBodyClass(): Unsubscribe {
    const sub = route$.onData((route) => {
        document.body.classList.toggle('rightPanelOpen', route != null)
        document.body.classList.toggle('rightPanelChatOpen', route === 'chat')
    })
    return () => {
        sub()
        document.body.classList.remove('rightPanelOpen')
        document.body.classList.remove('rightPanelChatOpen')
    }
}
